import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getSpan, type Span } from "../api/client";
import type { ServiceSlug } from "../api/client";

export interface UseSpanOptions {
  serviceSlug: ServiceSlug | null;
  spanId: string | null | undefined;
  refreshToken?: number;
  enabled?: boolean;
}

export interface UseSpanResult {
  span: Span | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook for fetching a single span by ID.
 * This does not require a time range since span_id should be unique.
 *
 * @param options - Configuration options for fetching a span
 * @returns Object containing span, loading state, and error
 */
export function useSpan({
  serviceSlug,
  spanId,
  refreshToken = 0,
  enabled = true,
}: UseSpanOptions): UseSpanResult {
  const query = useQuery({
    queryKey: ["span", serviceSlug, spanId, refreshToken],
    enabled: !!serviceSlug && !!spanId && enabled,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => getSpan(spanId!, serviceSlug!, signal),
  });

  if (!serviceSlug || !spanId || !enabled) {
    return { span: null, loading: false, error: null };
  }

  const error =
    query.error instanceof Error
      ? query.error.message
      : query.error
        ? "Failed to load span"
        : null;
  return { span: query.data ?? null, loading: query.isLoading, error };
}
